
import React, { useState, useEffect, useContext } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import Notifications from '../components/Notifications';
import { getProfilePhotoUrl } from '../config';
import { toast } from '../components/Toast';
import { Camera, Settings, Rocket, Clapperboard, Users, ClipboardList, Coins, BarChart2, Shield, Clock, Sparkles, XCircle, FileText, Paperclip, User, MapPin, Phone, Globe, Target, Calendar, Edit2, Save, Briefcase, Bell } from 'lucide-react';

const Profile = () => {
  const { userId } = useParams(); // Si userId existe, on regarde le profil d'un autre
  const { user: currentUser } = useContext(AuthContext);
  const isOwnProfile = !userId || (currentUser && userId === String(currentUser.id));
  
  const [user, setUser] = useState(null);
  const [authorRequest, setAuthorRequest] = useState(null);
  const [canMakeNewRequest, setCanMakeNewRequest] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    bio: '',
    localisation: '',
    telephone: '',
    competences: '',
    site_web: ''
  });
  const [requestForm, setRequestForm] = useState({
    nom_complet: '',
    date_naissance: '',
    adresse: '',
    telephone: '',
    piece_identite: null
  });
  const [stats, setStats] = useState({ projects: 0, applications: 0, services: 0 });
  
  useEffect(() => {
    loadProfile();
    if (isOwnProfile) {
      loadStats();
      checkAuthorRequest();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, isOwnProfile]);

  // Initialiser le formulaire de profil quand l'utilisateur est chargé
  useEffect(() => {
    if (user && isOwnProfile) {
      setProfileForm({
        bio: user.bio || '',
        localisation: user.localisation || '',
        telephone: user.telephone || '',
        competences: user.competences || '',
        site_web: user.site_web || ''
      });
    }
  }, [user, isOwnProfile]);

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.put(`/users/${user.id}`, profileForm);
      setUser({ ...user, ...profileForm });
      setEditingProfile(false);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast.error('Erreur lors de la sauvegarde du profil');
    } finally {
      setSavingProfile(false);
    }
  };

  const loadProfile = async () => {
    try {
      if (userId && !isOwnProfile) {
        // Charger le profil d'un autre utilisateur
        const res = await api.get(`/users/${userId}`);
        setUser(res.data);
      } else {
        // Charger son propre profil
        const res = await api.get('/auth/me');
        setUser(res.data.user);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const loadStats = async () => {
    try {
      const [projects, applications, services] = await Promise.all([
        api.get('/projects/my').catch(() => ({ data: [] })),
        api.get('/applications/my').catch(() => ({ data: [] })),
        api.get('/services/my').catch(() => ({ data: [] }))
      ]);
      setStats({
        projects: projects.data?.length || 0,
        applications: applications.data?.length || 0,
        services: services.data?.length || 0
      });
    } catch (err) {
      console.error(err);
    }
  };

  const checkAuthorRequest = async () => {
    try {
      const res = await api.get('/author-requests/my');
      if (res.data) {
        setAuthorRequest(res.data.request);
        setCanMakeNewRequest(res.data.can_make_new);
      }
    } catch (err) {
      // Pas de demande en cours
      setCanMakeNewRequest(true);
    }
  };

  const handleFormChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'piece_identite') {
      setRequestForm(prev => ({ ...prev, piece_identite: files[0] }));
    } else {
      setRequestForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Vérifier le type
    if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
      toast.warning('Format non autorisé. Utilisez JPG, PNG, GIF ou WebP');
      return;
    }
    
    // Vérifier la taille (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.warning('Fichier trop volumineux (max 5Mo)');
      return;
    }
    
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('photo', file);
      
      const res = await api.post(`/users/${user.id}/photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Mettre à jour l'utilisateur avec la nouvelle photo
      setUser(prev => ({ ...prev, photo_profil: res.data.photo_profil }));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors de l\'upload');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const requestAuthorRole = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!requestForm.nom_complet || !requestForm.date_naissance || !requestForm.telephone) {
      toast.warning('Veuillez remplir tous les champs obligatoires');
      return;
    }
    if (!requestForm.piece_identite) {
      toast.warning('Veuillez joindre votre pièce d\'identité');
      return;
    }
    
    setRequestLoading(true);
    try {
      const formData = new FormData();
      formData.append('nom_complet', requestForm.nom_complet);
      formData.append('date_naissance', requestForm.date_naissance);
      formData.append('adresse', requestForm.adresse);
      formData.append('telephone', requestForm.telephone);
      formData.append('piece_identite', requestForm.piece_identite);
      
      await api.post('/author-requests', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setShowRequestForm(false);
      checkAuthorRequest();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors de la demande');
    } finally {
      setRequestLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-10 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Profile Card */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
          <div className="flex flex-col md:flex-row items-center gap-6">
            {/* Photo de profil avec upload */}
            <div className="relative group">
              {user.photo_profil ? (
                <img 
                  src={getProfilePhotoUrl(user.photo_profil)}
                  alt={user.nom}
                  className="w-24 h-24 rounded-full object-cover border-4 border-indigo-200"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center">
                  <span className="text-4xl text-white font-bold">{user.nom?.[0]}</span>
                </div>
              )}
              {isOwnProfile && (
                <label className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full opacity-0 group-hover:opacity-100 cursor-pointer transition">
                  {uploadingPhoto ? (
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent"></div>
                  ) : (
                    <Camera size={24} className="text-white" />
                  )}
                  <input 
                    type="file" 
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handlePhotoUpload}
                    className="hidden"
                    disabled={uploadingPhoto}
                  />
                </label>
              )}
            </div>
            <div className="flex-1 text-center md:text-left">
              <h1 className="text-2xl font-bold text-gray-800">{user.nom}</h1>
              {isOwnProfile && <p className="text-gray-500">{user.email}</p>}
              <div className="flex items-center justify-center md:justify-start gap-2 mt-2">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  user.role === 'admin' ? 'bg-red-100 text-red-600' :
                  user.role === 'author' ? 'bg-green-100 text-green-600' :
                  'bg-blue-100 text-blue-600'
                }`}>
                  {user.role === 'admin' ? 'Administrateur' :
                   user.role === 'author' ? 'Auteur' : 'Utilisateur'}
                </span>
              </div>
            </div>
            {isOwnProfile && (
              <Link
                to="/settings"
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
              >
                <Settings size={14} className="inline mr-1" /> Paramètres
              </Link>
            )}
          </div>
        </div>

        {/* Author Request - Only for own profile - PROMINENT BANNER - MOVED TO TOP */}
        {isOwnProfile && user.role === 'user' && (
          <div className="relative overflow-hidden rounded-2xl shadow-xl mb-6 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500">
            {/* Background decorative elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2"></div>
            <div className="absolute top-1/2 left-1/2 w-32 h-32 bg-white/5 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
            
            <div className="relative z-10 p-6 md:p-8">
              {/* Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                  <Rocket size={30} />
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-white">Devenir Auteur</h2>
                  <p className="text-white/80 text-sm">Passez au niveau supérieur !</p>
                </div>
              </div>
              
              {/* Description */}
              <p className="text-white/90 text-lg mb-6 max-w-2xl">
                Vous avez des projets créatifs ? Devenez <span className="font-bold text-yellow-300">Auteur</span> et 
                débloquez toutes les fonctionnalités pour réaliser vos ambitions !
              </p>
              
              {/* Benefits grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                <div className="bg-white/15 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <div className="flex items-center gap-3 mb-2">
                    <Clapperboard size={24} />
                    <h3 className="font-semibold text-white">Créer des projets</h3>
                  </div>
                  <p className="text-white/80 text-sm">Publiez vos projets et recrutez les meilleurs talents</p>
                </div>
                
                <div className="bg-white/15 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <div className="flex items-center gap-3 mb-2">
                    <Users size={24} />
                    <h3 className="font-semibold text-white">Gérer des équipes</h3>
                  </div>
                  <p className="text-white/80 text-sm">Constituez votre équipe et gérez les collaborations</p>
                </div>
                
                <div className="bg-white/15 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <div className="flex items-center gap-3 mb-2">
                    <ClipboardList size={24} />
                    <h3 className="font-semibold text-white">Publier des appels</h3>
                  </div>
                  <p className="text-white/80 text-sm">Lancez des appels à candidatures pour vos rôles</p>
                </div>
                
                <div className="bg-white/15 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <div className="flex items-center gap-3 mb-2">
                    <Coins size={24} />
                    <h3 className="font-semibold text-white">Gérer les finances</h3>
                  </div>
                  <p className="text-white/80 text-sm">Budgets, paiements et facturation intégrés</p>
                </div>
                
                <div className="bg-white/15 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <div className="flex items-center gap-3 mb-2">
                    <BarChart2 size={24} />
                    <h3 className="font-semibold text-white">Statistiques</h3>
                  </div>
                  <p className="text-white/80 text-sm">Suivez les performances de vos projets</p>
                </div>
                
                <div className="bg-white/15 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <div className="flex items-center gap-3 mb-2">
                    <Shield size={24} />
                    <h3 className="font-semibold text-white">Badge vérifié</h3>
                  </div>
                  <p className="text-white/80 text-sm">Profil authentifié pour plus de crédibilité</p>
                </div>
              </div>
              
              {/* Status messages */}
              {/* Invitation à ajouter une photo de profil */}
              {!user.photo_profil && canMakeNewRequest && (
                <div className="p-4 rounded-xl bg-amber-500/20 border border-amber-400/30 backdrop-blur-sm mb-4">
                  <div className="flex items-start gap-3">
                    <Camera size={24} />
                    <div>
                      <p className="font-semibold text-white">Ajoutez d'abord une photo de profil !</p>
                      <p className="text-white/80 text-sm mt-1">
                        Une photo de profil inspire confiance et augmente vos chances d'être accepté comme auteur.
                        Survolez votre avatar ci-dessus pour ajouter une photo.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {authorRequest && authorRequest.statut === 'en_attente' && (
                <div className="p-4 rounded-xl bg-yellow-500/20 border border-yellow-400/30 backdrop-blur-sm mb-4">
                  <div className="flex items-center gap-3">
                    <Clock size={24} className="animate-pulse" />
                    <div>
                      <p className="font-semibold text-white">Demande en cours d'examen</p>
                      <p className="text-white/80 text-sm">Notre équipe étudie votre dossier. Vous serez notifié bientôt !</p>
                    </div>
                  </div>
                </div>
              )}
              
              {authorRequest && authorRequest.statut === 'valide' && (
                <div className="p-4 rounded-xl bg-green-500/20 border border-green-400/30 backdrop-blur-sm mb-4">
                  <div className="flex items-center gap-3">
                    <Sparkles size={24} />
                    <div>
                      <p className="font-semibold text-white">Félicitations ! Votre demande a été acceptée.</p>
                      {authorRequest.justification_admin && (
                        <p className="text-white/80 text-sm mt-1">Note: {authorRequest.justification_admin}</p>
                      )}
                      <p className="text-white/80 text-sm mt-2">Reconnectez-vous pour activer votre nouveau rôle.</p>
                    </div>
                  </div>
                </div>
              )}
              
              {authorRequest && authorRequest.statut === 'refuse' && (
                <div className="p-4 rounded-xl bg-red-500/20 border border-red-400/30 backdrop-blur-sm mb-4">
                  <div className="flex items-start gap-3">
                    <XCircle size={24} />
                    <div>
                      <p className="font-semibold text-white">Votre demande a été refusée</p>
                      {authorRequest.justification_admin && (
                        <p className="text-white/80 text-sm mt-1">Motif: {authorRequest.justification_admin}</p>
                      )}
                      <p className="text-white/80 text-sm mt-2">Vous pouvez soumettre une nouvelle demande après avoir corrigé les points mentionnés.</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* CTA Button */}
              {(canMakeNewRequest || (!authorRequest && canMakeNewRequest)) && !showRequestForm && (
                <button
                  onClick={() => setShowRequestForm(true)}
                  className="group relative inline-flex items-center gap-3 px-8 py-4 bg-white text-indigo-600 font-bold text-lg rounded-xl hover:bg-yellow-300 hover:text-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
                >
                  <Sparkles size={24} className="group-hover:animate-bounce" />
                  <span>{authorRequest?.statut === 'refuse' ? 'Retenter ma chance' : 'Devenir Auteur maintenant'}</span>
                  <span className="text-xl">→</span>
                </button>
              )}
            
            {showRequestForm && (
              <form onSubmit={requestAuthorRole} className="mt-6 p-6 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
                <div className="flex items-center gap-3 mb-6">
                  <FileText size={24} />
                  <h3 className="font-bold text-white text-lg">Formulaire pour être identifié</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-white/90 mb-2">
                      Nom complet *
                    </label>
                    <input
                      type="text"
                      name="nom_complet"
                      value={requestForm.nom_complet}
                      onChange={handleFormChange}
                      placeholder="Votre nom et prénom"
                      className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-xl text-white placeholder-white/50 focus:ring-2 focus:ring-white/50 focus:border-transparent"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-white/90 mb-2">
                      Date de naissance *
                    </label>
                    <input
                      type="date"
                      name="date_naissance"
                      value={requestForm.date_naissance}
                      onChange={handleFormChange}
                      className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-xl text-white focus:ring-2 focus:ring-white/50 focus:border-transparent"
                      required
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-white/90 mb-2">
                      Téléphone *
                    </label>
                    <input
                      type="tel"
                      name="telephone"
                      value={requestForm.telephone}
                      onChange={handleFormChange}
                      placeholder="+225 6XX XXX XXX"
                      className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-xl text-white placeholder-white/50 focus:ring-2 focus:ring-white/50 focus:border-transparent"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-white/90 mb-2">
                      Adresse
                    </label>
                    <input
                      type="text"
                      name="adresse"
                      value={requestForm.adresse}
                      onChange={handleFormChange}
                      placeholder="Votre ville, pays"
                      className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-xl text-white placeholder-white/50 focus:ring-2 focus:ring-white/50 focus:border-transparent"
                    />
                  </div>
                </div>
                
                <div className="mb-6">
                  <label className="block text-sm font-medium text-white/90 mb-2">
                    Pièce d'identité (CIN, Passeport) *
                  </label>
                  <div className="relative">
                    <input
                      type="file"
                      name="piece_identite"
                      onChange={handleFormChange}
                      accept=".jpg,.jpeg,.png,.pdf"
                      className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-xl text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-white/30 file:text-white file:font-medium hover:file:bg-white/40 focus:ring-2 focus:ring-white/50 focus:border-transparent"
                      required
                    />
                  </div>
                  <p className="text-xs text-white/60 mt-2 flex items-center gap-1">
                    <Paperclip size={14} /> Formats acceptés: JPG, PNG, PDF (max 5MB)
                  </p>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-white/20">
                  <button
                    type="submit"
                    disabled={requestLoading}
                    className="flex-1 px-6 py-3 bg-white text-indigo-600 font-bold rounded-xl hover:bg-yellow-300 hover:text-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {requestLoading ? (
                      <>
                        <Clock size={16} className="animate-spin" />
                        <span>Envoi en cours...</span>
                      </>
                    ) : (
                      <>
                        <Rocket size={16} />
                        <span>Envoyer ma demande</span>
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowRequestForm(false)}
                    className="px-6 py-3 bg-white/20 text-white font-medium rounded-xl hover:bg-white/30 transition border border-white/30"
                  >
                    Annuler
                  </button>
                </div>
              </form>
            )}
            </div>
          </div>
        )}

        {/* Bio et Informations - Pour voir les autres profils */}
        {!isOwnProfile && (user.bio || user.localisation || user.competences || user.site_web) && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4"><User size={20} className="inline mr-1" /> À propos</h2>
            
            {user.bio && (
              <div className="mb-4">
                <p className="text-gray-600 whitespace-pre-line">{user.bio}</p>
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {user.localisation && (
                <div className="flex items-center gap-3 text-gray-600">
                  <MapPin size={20} />
                  <span>{user.localisation}</span>
                </div>
              )}
              
              {user.telephone && (
                <div className="flex items-center gap-3 text-gray-600">
                  <Phone size={20} />
                  <a href={`tel:${user.telephone}`} className="hover:text-indigo-600 transition">
                    {user.telephone}
                  </a>
                </div>
              )}
              
              {user.site_web && (
                <div className="flex items-center gap-3 text-gray-600">
                  <Globe size={20} />
                  <a 
                    href={user.site_web.startsWith('http') ? user.site_web : `https://${user.site_web}`}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="hover:text-indigo-600 transition truncate"
                  >
                    {user.site_web.replace(/^https?:\/\//, '')}
                  </a>
                </div>
              )}
            </div>
            
            {user.competences && (
              <div className="mt-4">
                <h3 className="text-sm font-medium text-gray-500 mb-2"><Target size={14} className="inline mr-1" /> Compétences</h3>
                <div className="flex flex-wrap gap-2">
                  {user.competences.split(',').map((comp, idx) => (
                    <span 
                      key={idx}
                      className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm"
                    >
                      {comp.trim()}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Public Stats - For viewing other profiles */}
        {!isOwnProfile && user.stats && (
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-white rounded-xl shadow p-4 text-center">
              <div className="text-3xl font-bold text-indigo-600">{user.stats.projects_joined || 0}</div>
              <div className="text-gray-500 text-sm">Projets rejoints</div>
            </div>
            {user.stats.projects_created !== undefined && (
              <div className="bg-white rounded-xl shadow p-4 text-center">
                <div className="text-3xl font-bold text-green-600">{user.stats.projects_created}</div>
                <div className="text-gray-500 text-sm">Projets créés</div>
              </div>
            )}
          </div>
        )}

        {/* Projects joined - For viewing other profiles */}
        {!isOwnProfile && user.projects && user.projects.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6 overflow-hidden">
            <h2 className="text-xl font-semibold text-gray-800 mb-4"><Clapperboard size={20} className="inline mr-1" /> Projets rejoints</h2>
            <div className="space-y-3">
              {user.projects.map((project) => (
                <Link 
                  key={project.id} 
                  to={`/project/${project.id}`}
                  className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg hover:bg-indigo-50 transition overflow-hidden"
                >
                  <div className="w-10 h-10 min-w-[2.5rem] rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold flex-shrink-0">
                    {project.titre?.[0] || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-800 truncate">{project.titre}</p>
                    <p className="text-sm text-gray-500 truncate">{project.description}</p>
                  </div>
                  <span className="px-2 py-1 bg-indigo-100 text-indigo-600 text-xs rounded flex-shrink-0 whitespace-nowrap">
                    {project.role_type}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Projects created by author - For viewing other profiles */}
        {!isOwnProfile && user.projects_created && user.projects_created.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4"><Sparkles size={20} className="inline mr-1" /> Projets créés</h2>
            <div className="space-y-3">
              {user.projects_created.map((project) => (
                <Link 
                  key={project.id} 
                  to={`/project/${project.id}`}
                  className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg hover:bg-green-50 transition min-w-0"
                >
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center text-green-600 font-bold flex-shrink-0">
                    {project.titre?.[0] || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-800 truncate">{project.titre}</p>
                    <p className="text-sm text-gray-500 truncate">{project.description}</p>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded flex-shrink-0 whitespace-nowrap ${
                    project.statut === 'actif' ? 'bg-green-100 text-green-600' : 
                    project.statut === 'archive' ? 'bg-gray-100 text-gray-600' : 
                    'bg-yellow-100 text-yellow-600'
                  }`}>
                    {project.statut}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Member since - For viewing other profiles */}
        {!isOwnProfile && user.date_inscription && (
          <div className="text-center text-gray-500 text-sm mb-6">
            <Calendar size={14} className="inline mr-1" /> Membre depuis {new Date(user.date_inscription).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
          </div>
        )}

        {/* Mon Profil - Section d'édition pour son propre profil */}
        {isOwnProfile && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-800"><User size={20} className="inline mr-1" /> Mon Profil</h2>
              {!editingProfile ? (
                <button
                  onClick={() => setEditingProfile(true)}
                  className="px-4 py-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                >
                  <Edit2 size={14} className="inline mr-1" /> Modifier
                </button>
              ) : (
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setEditingProfile(false);
                      setProfileForm({
                        bio: user.bio || '',
                        localisation: user.localisation || '',
                        telephone: user.telephone || '',
                        competences: user.competences || '',
                        site_web: user.site_web || ''
                      });
                    }}
                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition"
                    disabled={savingProfile}
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleSaveProfile}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition disabled:opacity-50"
                    disabled={savingProfile}
                  >
                    {savingProfile ? <><Clock size={14} className="inline mr-1" /> Enregistrement...</> : <><Save size={14} className="inline mr-1" /> Enregistrer</>}
                  </button>
                </div>
              )}
            </div>

            {!editingProfile ? (
              // Mode affichage
              <div>
                {user.bio ? (
                  <p className="text-gray-600 whitespace-pre-line mb-4">{user.bio}</p>
                ) : (
                  <p className="text-gray-400 italic mb-4">Aucune bio ajoutée. Cliquez sur "Modifier" pour vous présenter.</p>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="flex items-center gap-3 text-gray-600">
                    <MapPin size={20} />
                    <span>{user.localisation || <span className="text-gray-400 italic">Non renseignée</span>}</span>
                  </div>
                  
                  <div className="flex items-center gap-3 text-gray-600">
                    <Phone size={20} />
                    {user.telephone ? (
                      <a href={`tel:${user.telephone}`} className="hover:text-indigo-600 transition">
                        {user.telephone}
                      </a>
                    ) : (
                      <span className="text-gray-400 italic">Non renseigné</span>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-3 text-gray-600">
                    <Globe size={20} />
                    {user.site_web ? (
                      <a 
                        href={user.site_web.startsWith('http') ? user.site_web : `https://${user.site_web}`}
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="hover:text-indigo-600 transition truncate"
                      >
                        {user.site_web.replace(/^https?:\/\//, '')}
                      </a>
                    ) : (
                      <span className="text-gray-400 italic">Non renseigné</span>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2"><Target size={14} className="inline mr-1" /> Compétences</h3>
                  {user.competences ? (
                    <div className="flex flex-wrap gap-2">
                      {user.competences.split(',').map((comp, idx) => (
                        <span 
                          key={idx}
                          className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm"
                        >
                          {comp.trim()}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400 italic text-sm">Aucune compétence ajoutée</p>
                  )}
                </div>
              </div>
            ) : (
              // Mode édition
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                  <textarea
                    value={profileForm.bio}
                    onChange={(e) => setProfileForm({...profileForm, bio: e.target.value})}
                    placeholder="Présentez-vous en quelques lignes..."
                    rows={3}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1"><MapPin size={14} className="inline mr-1" /> Localisation</label>
                    <input
                      type="text"
                      value={profileForm.localisation}
                      onChange={(e) => setProfileForm({...profileForm, localisation: e.target.value})}
                      placeholder="Ex: Abidjan, Côte d'Ivoire"
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1"><Phone size={14} className="inline mr-1" /> Téléphone</label>
                    <input
                      type="tel"
                      value={profileForm.telephone}
                      onChange={(e) => setProfileForm({...profileForm, telephone: e.target.value})}
                      placeholder="Ex: +225 07 00 00 00"
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1"><Globe size={14} className="inline mr-1" /> Site web</label>
                    <input
                      type="url"
                      value={profileForm.site_web}
                      onChange={(e) => setProfileForm({...profileForm, site_web: e.target.value})}
                      placeholder="Ex: www.monsite.com"
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1"><Target size={14} className="inline mr-1" /> Compétences</label>
                  <input
                    type="text"
                    value={profileForm.competences}
                    onChange={(e) => setProfileForm({...profileForm, competences: e.target.value})}
                    placeholder="Ex: Design, Marketing, Gestion de projet (séparées par des virgules)"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Séparez vos compétences par des virgules</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Stats - Only for own profile */}
        {isOwnProfile && (
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-xl shadow p-4 text-center">
              <div className="text-3xl font-bold text-indigo-600">{stats.projects}</div>
              <div className="text-gray-500 text-sm">Projets</div>
            </div>
            <div className="bg-white rounded-xl shadow p-4 text-center">
              <div className="text-3xl font-bold text-indigo-600">{stats.applications}</div>
              <div className="text-gray-500 text-sm">Candidatures</div>
            </div>
            <div className="bg-white rounded-xl shadow p-4 text-center">
              <div className="text-3xl font-bold text-indigo-600">{stats.services}</div>
              <div className="text-gray-500 text-sm">Services</div>
            </div>
          </div>
        )}

        {/* Quick Links */}
        {/* Quick Links - Only for own profile */}
        {isOwnProfile && (
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <Link
              to="/my-applications"
              className="bg-white rounded-xl shadow p-6 hover:shadow-lg transition flex items-center gap-4"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-2xl"><FileText size={24} /></div>
              <div>
                <h3 className="font-semibold text-gray-800">Mes candidatures</h3>
                <p className="text-sm text-gray-500">Suivre mes candidatures</p>
              </div>
            </Link>
            
            {(user.role === 'author' || user.role === 'admin') && (
              <Link
                to="/author"
                className="bg-white rounded-xl shadow p-6 hover:shadow-lg transition flex items-center gap-4"
              >
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center text-2xl"><Clapperboard size={24} /></div>
                <div>
                  <h3 className="font-semibold text-gray-800">Mes projets</h3>
                  <p className="text-sm text-gray-500">Gérer mes projets</p>
                </div>
              </Link>
            )}
            
            <Link
              to="/services"
              className="bg-white rounded-xl shadow p-6 hover:shadow-lg transition flex items-center gap-4"
            >
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center text-2xl"><Briefcase size={24} /></div>
              <div>
                <h3 className="font-semibold text-gray-800">Services</h3>
                <p className="text-sm text-gray-500">Parcourir les services</p>
              </div>
            </Link>
            
            <Link
              to="/settings"
              className="bg-white rounded-xl shadow p-6 hover:shadow-lg transition flex items-center gap-4"
            >
              <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center text-2xl"><Settings size={24} /></div>
              <div>
                <h3 className="font-semibold text-gray-800">Paramètres</h3>
                <p className="text-sm text-gray-500">Paramètres du compte</p>
              </div>
            </Link>
          </div>
        )}

        {/* Notifications - Only for own profile */}
        {isOwnProfile && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4"><Bell size={20} className="inline mr-1" /> Notifications récentes</h2>
            <Notifications />
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
